import React, { useEffect, useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import axios from "axios";
import { fmtUSD } from "../../utils/currency";
import CRMPipelineView from "./CRMPipelineView";
import Contact360Drawer from "./Contact360Drawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { id: "prospect", label: "Prospect", color: "bg-slate-400", border: "border-slate-200" },
  { id: "onboarding", label: "Onboarding", color: "bg-blue-400", border: "border-blue-200" },
  { id: "active", label: "Active", color: "bg-emerald-400", border: "border-emerald-200" },
  { id: "reviewing", label: "Reviewing Deals", color: "bg-amber-400", border: "border-amber-200" },
  { id: "funding", label: "Funding", color: "bg-green-500", border: "border-green-200" },
  { id: "repeat", label: "Repeat Lender", color: "bg-purple-500", border: "border-purple-200" },
];

function derivePipelineStage(l) {
  const override = l.extra?.pipeline_stage;
  if (override && STAGES.some(s => s.id === override)) return override;
  // Check crm_stage from backend
  if (l.crm_stage && STAGES.some(s => s.id === l.crm_stage)) return l.crm_stage;
  // Auto-derive
  const funded = l.deals_funded || 0;
  const reviewed = l.deals_reviewed || 0;
  const ts = l.ts_submitted || 0;
  if (funded > 1) return "repeat";
  if (funded > 0 || ts > 0) return "funding";
  if (reviewed > 0) return "reviewing";
  if (l.approval_status === "approved" && l.status === "active") return "active";
  if (l.approval_status === "approved") return "onboarding";
  return "prospect";
}

const TABLE_COLUMNS = [
  { key: "full_name", label: "Name", render: l => (
    <div>
      <div className="text-xs font-semibold text-[#0A0A0A]">{l.full_name || l.entity_name || "—"}</div>
      <div className="text-[10px] text-[#888]">{l.email}</div>
    </div>
  )},
  { key: "company", label: "Company", render: l => <span className="text-xs text-[#555]">{l.company || l.entity_name || "—"}</span> },
  { key: "deals_reviewed", label: "Reviewed", render: l => <span className="text-xs">{l.deals_reviewed || 0}</span> },
  { key: "ts_submitted", label: "Term Sheets", render: l => <span className="text-xs text-blue-600 font-semibold">{l.ts_submitted || 0}</span> },
  { key: "deals_funded", label: "Funded", render: l => <span className="text-xs text-emerald-600 font-semibold">{l.deals_funded || 0}</span> },
  { key: "avg_loan", label: "Avg Loan", render: l => <span className="text-xs text-[#b89d5a]">{fmtUSD(l.avg_loan || 0)}</span> },
];

export default function CRMLenders({ onViewDeal, onEntityNavigate }) {
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/lenders`, { headers });
      setLenders(res.data.lenders || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStageChange = (userId, newStage) => {
    setLenders(prev => prev.map(l =>
      l.user_id === userId ? { ...l, extra: { ...(l.extra || {}), pipeline_stage: newStage } } : l
    ));
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="crm-lenders-loading">
      <Loader2 className="h-8 w-8 animate-spin text-emerald-700" />
    </div>
  );

  const renderCardContent = (l) => (
    <div className="mt-2 space-y-1">
      {l.company && <div className="text-[10px] text-[#666] truncate">{l.company}</div>}
      <div className="flex items-center gap-2 flex-wrap">
        {l.deals_reviewed > 0 && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-100">
            {l.deals_reviewed} reviewed
          </span>
        )}
        {l.ts_submitted > 0 && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 border border-purple-100">
            {l.ts_submitted} terms
          </span>
        )}
        {l.deals_funded > 0 && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-50 text-green-700 border border-green-100">
            {l.deals_funded} funded
          </span>
        )}
      </div>
      {l.avg_loan > 0 && (
        <div className="text-[10px] font-semibold text-[#b89d5a]">Avg: {fmtUSD(l.avg_loan)}</div>
      )}
    </div>
  );

  return (
    <div data-testid="crm-lenders">
      <CRMPipelineView
        title="Lenders"
        subtitle="Lender pipeline, deal review activity, and funding performance"
        stages={STAGES}
        items={lenders}
        deriveStageFn={derivePipelineStage}
        renderCardContent={renderCardContent}
        tableColumns={TABLE_COLUMNS}
        onSelectUser={setSelectedUser}
        onStageChange={handleStageChange}
        entityType="lender"
        testIdPrefix="crm-l"
      />

      <Contact360Drawer open={!!selectedUser} onClose={() => setSelectedUser(null)}
        userId={selectedUser} userType="lender" onViewDeal={onViewDeal} onEntityNavigate={onEntityNavigate} />
    </div>
  );
}
